
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import LocationSearchForm from "../components/LocationSearchForm";
import PasswordProtection from "../components/PasswordProtection";
import { MapPin, Calendar, Trash2, ExternalLink, Check, X } from "lucide-react";

interface SuggestedLocation {
  id: string;
  name: string;
  address: string;
  placeId: string;
  types: string[];
  coordinates: { lat: number; lng: number } | null;
  suggestedAt: string;
}

const SuggestedSpots = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [suggestedLocations, setSuggestedLocations] = useState<SuggestedLocation[]>([]);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Check if user is already authenticated
    const authenticated = localStorage.getItem('spotsAuthenticated') === 'true';
    setIsAuthenticated(authenticated);

    if (authenticated) {
      // Load suggested locations from localStorage
      const saved = localStorage.getItem('suggestedSpots');
      if (saved) {
        setSuggestedLocations(JSON.parse(saved));
      }
    }
  }, []);

  const handleAuthSuccess = () => {
    setIsAuthenticated(true);
    // Load suggested locations after authentication
    const saved = localStorage.getItem('suggestedSpots');
    if (saved) {
      setSuggestedLocations(JSON.parse(saved));
    }
  };

  const showMessage = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleLocationSelect = (location: any) => {
    const newLocation: SuggestedLocation = {
      id: Date.now().toString(),
      name: location.name,
      address: location.address,
      placeId: location.placeId,
      types: location.types || [],
      coordinates: location.coordinates,
      suggestedAt: new Date().toISOString()
    };

    const updatedLocations = [newLocation, ...suggestedLocations];
    setSuggestedLocations(updatedLocations);
    localStorage.setItem('suggestedSpots', JSON.stringify(updatedLocations));
    showMessage('Location added to suggestions!');
  };

  const approveLocation = (id: string) => {
    const location = suggestedLocations.find(loc => loc.id === id);
    if (!location) return;

    // Add to approved spots
    const approvedSpots = JSON.parse(localStorage.getItem('approvedSpots') || '[]');
    const approvedLocation = { ...location, approvedAt: new Date().toISOString() };
    const updatedApproved = [approvedLocation, ...approvedSpots];
    localStorage.setItem('approvedSpots', JSON.stringify(updatedApproved));

    // Remove from suggested spots
    const updatedLocations = suggestedLocations.filter(loc => loc.id !== id);
    setSuggestedLocations(updatedLocations);
    localStorage.setItem('suggestedSpots', JSON.stringify(updatedLocations));
    
    showMessage('Spot approved and added to the main map!');
  };

  const rejectLocation = (id: string) => {
    const updatedLocations = suggestedLocations.filter(loc => loc.id !== id);
    setSuggestedLocations(updatedLocations);
    localStorage.setItem('suggestedSpots', JSON.stringify(updatedLocations));
    showMessage('Spot rejected and removed.');
  };

  const removeLocation = (id: string) => {
    const updatedLocations = suggestedLocations.filter(loc => loc.id !== id);
    setSuggestedLocations(updatedLocations);
    localStorage.setItem('suggestedSpots', JSON.stringify(updatedLocations));
    showMessage('Spot removed from suggestions.');
  };

  const generateGoogleMapsUrl = (address: string) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const getLocationTypeDisplay = (types: string[]) => {
    const relevantTypes = types.filter(type => 
      ['cafe', 'restaurant', 'library', 'lodging', 'establishment'].includes(type)
    );
    return relevantTypes.length > 0 ? relevantTypes[0].replace('_', ' ') : 'Location';
  };

  if (!isAuthenticated) {
    return <PasswordProtection onSuccess={handleAuthSuccess} />;
  }

  return (
    <div className="min-h-screen bg-[#f9f8f5]">
      <Header />
      
      <main className="pt-20">
        <div className="max-w-[1440px] mx-auto px-4 desktop:px-24 laptop:px-20 tablet:px-12 mobile:px-6 py-12">
          {successMessage && (
            <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
              {successMessage}
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h1 className="text-4xl desktop:text-5xl laptop:text-4xl tablet:text-3xl mobile:text-2xl font-bold text-[#374151] mb-4">
              Suggest a Quiet Spot
            </h1>
            <p className="text-lg desktop:text-xl laptop:text-lg tablet:text-base mobile:text-sm text-gray-700 max-w-4xl mx-auto mb-8">
              Know a great place for video calls or quiet work? Search and add it to our community suggestions.
            </p>

            <div className="flex justify-center mb-12">
              <LocationSearchForm onLocationSelect={handleLocationSelect} />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-2xl font-semibold text-[#374151] mb-6">
              Pending Suggestions ({suggestedLocations.length})
            </h2>

            {suggestedLocations.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <MapPin size={48} className="text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No suggestions yet. Start by searching for a location above!</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {suggestedLocations.map((location, index) => (
                  <motion.div
                    key={location.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-[#374151] mb-1">
                          {location.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2 capitalize">
                          {getLocationTypeDisplay(location.types)}
                        </p>
                        <p className="text-sm text-gray-500 mb-3">
                          {location.address}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-400">
                          <div className="flex items-center gap-1">
                            <Calendar size={12} />
                            <span>
                              Suggested {new Date(location.suggestedAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => approveLocation(location.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white text-xs rounded-md hover:bg-green-600 transition-colors duration-200"
                          title="Approve and add to main map"
                        >
                          <Check size={12} />
                          Approve
                        </button>
                        <button
                          onClick={() => rejectLocation(location.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white text-xs rounded-md hover:bg-red-600 transition-colors duration-200"
                          title="Reject suggestion"
                        >
                          <X size={12} />
                          Reject
                        </button>
                        <a
                          href={generateGoogleMapsUrl(location.address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white text-xs rounded-md hover:bg-blue-600 transition-colors duration-200"
                        >
                          View on Maps
                          <ExternalLink size={12} />
                        </a>
                        <button
                          onClick={() => removeLocation(location.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors duration-200"
                          title="Remove suggestion"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SuggestedSpots;
